'use client';

import { useTranslation } from 'react-i18next';
import {
  useMediaSource,
  useBlockPart,
  type ChannelCard as ChannelCardData,
  type ChannelSort,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ChannelCard } from './ChannelCard';
import { sectionPad, sectionInner } from '../_shared/styles';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * /video/channels — discover channels. Grid of {@link ChannelCard}s
 * with sort + pagination.
 */
export interface ChannelDiscoveryLayoutProps {
  title?: string;
  sort?: ChannelSort;
  limit?: number;
  channelHrefTemplate?: string;
}

export function ChannelDiscoveryLayout({
  title,
  sort = 'top-subscribed',
  limit = 24,
  channelHrefTemplate,
}: ChannelDiscoveryLayoutProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const heading = title ?? t('shared.label.channels');
  const media = useMediaSource();
  const state = useAsyncResource<ChannelCardData[]>(
    async (signal) => {
      if (!media) return null;
      const list = await media.channels.list({ sort, limit, signal });
      return list.length > 0 ? list : null;
    },
    [media, sort, limit],
  );

  if (!media) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="ChannelDiscoveryLayout" adapterName="MediaSource" />
        </div>
      </BlockRoot>
    );
  }
  if (state.kind !== 'loaded') return null;

  return (
    <BlockRoot as="section" style={sectionPad}>
      <div style={sectionInner}>
        <h1
          {...part('title')}
          style={{ fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 700, marginBottom: 24 }}
        >
          {heading}
        </h1>
        <div
          {...part('list')}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
            gap: 16,
          }}
        >
          {state.data.map((c) => (
            <ChannelCard
              key={c.id}
              channel={c}
              {...(channelHrefTemplate ? { hrefTemplate: channelHrefTemplate } : {})}
            />
          ))}
        </div>
      </div>
    </BlockRoot>
  );
}
